import { AccessRights, DetermineAccessRightsParams, EnergyTypeValue } from 'store/access-rights/types.ts'
import { INITIAL_ACCESS_RIGHTS_STATE } from 'store/access-rights/constants.ts'
import { contractIsOlderThanGivenMonths, isActiveContract } from 'utils/contracts.ts'
import { CONTRACT_STATUSES, EL_PRODUCT_CODES, ENERGY_TYPES, SITUATIONS, TARGET_SERVICE_COMPONENTS } from '@boltenergy-be/shared'
import dayjs from 'dayjs'
import { log } from 'utils/logging.ts'
import { Criticality, Flow } from 'types/logging.ts'
import { checkHasFixedProduct } from 'utils/products.ts'

/**
 * Utility function to check if at least one energy type has a true value in the given EnergyTypeValue object
 * @param {EnergyTypeValue<boolean>} energyTypeBooleans
 * @returns {boolean}
 */
export const hasEnergyTypeValue = (energyTypeBooleans: EnergyTypeValue<boolean>) => {
  return Object.values(energyTypeBooleans).some(Boolean)
}

/**
 * Determines access rights based on the given contract and related data
 *
 * @param {DetermineAccessRightsParams} params - The parameters needed to determine access rights
 * @param {ServiceContractElectricity | null | undefined} params.electricity - The electricity service contract
 * @param {ServiceContractGas} [params.gas] - The gas service contract
 * @param {boolean} params.isProducer - Whether the account is a producer account
 * @param {string} [params.lastSettlementDate] - The last settlement date of the billing contract
 * @param {Opportunity} [params.currentOpportunity] - The current opportunity associated with the contract
 * @returns {AccessRights} The computed access rights for the contract
 *
 * @example
 * const accessRights = determineAccessRights({
 *   electricity: electricityContract,
 *   gas: gasContract,
 *   isProducer: false,
 *   lastSettlementDate: '2024-01-15',
 *   currentOpportunity: opportunity
 * })
 */
export const determineAccessRights = ({
  electricity,
  gas,
  isProducer,
  lastSettlementDate,
  currentOpportunity
}: DetermineAccessRightsParams): AccessRights => {
  try {
    // Return default access rights if contract is missing or if the structure is invalid
    if (!electricity || !electricity.detail || !electricity.deliveryPoint) {
      throw new Error('Can not determine access rights. Missing data')
    }

    // Electricity contract status conditions
    const activeOrEffectiveContractEL = isActiveContract(electricity)
    const effectiveContract = isActiveContract(electricity, [CONTRACT_STATUSES.effective])
    const atLeastAMonthOldEL = contractIsOlderThanGivenMonths(electricity, 1)
    const isBoltGo = electricity.detail.productCode === EL_PRODUCT_CODES.Go
    const canViewMeterReadingsEL = !isProducer && !electricity.detail.dynamicTariff

    // Gas contract status conditions
    const activeOrEffectiveContractNG = isActiveContract(gas)
    const atLeastAMonthOldNG = !!gas && contractIsOlderThanGivenMonths(gas, 1)
    const canViewMeterReadingsNG = !isProducer && !!gas

    // Specific bill shock exclusion conditions
    const canAccessBillingCycles = !isProducer && !isBoltGo && !electricity.detail.monthlyBilling

    // Inaccuracy due to solar panels
    const hasReversingCounter = electricity.deliveryPoint.serviceComponent === TARGET_SERVICE_COMPONENTS.compensatedOfftake
    const hasBothInjectionAndConsumption = electricity.deliveryPoint.serviceComponent === TARGET_SERVICE_COMPONENTS.commOfftakeAndInjection

    // Inaccuracy due to move
    const isCombinedOrCustomerSwitch = !!currentOpportunity && currentOpportunity.situation !== SITUATIONS.switch
    const lastSettlementDateYearAfterContractStart =
      !!lastSettlementDate && dayjs(lastSettlementDate).isAfter(dayjs(electricity.detail.contractualStartDate).add(1, 'year'))

    return {
      billingCycles: {
        canAccess: {
          current: canAccessBillingCycles,
          previous: canAccessBillingCycles
        },
        showContent: {
          current: canAccessBillingCycles && effectiveContract && atLeastAMonthOldEL,
          previous:
            canAccessBillingCycles &&
            ((effectiveContract && atLeastAMonthOldEL) || electricity.detail.status === CONTRACT_STATUSES.terminated)
        },
        inaccuracyReason:
          hasReversingCounter || hasBothInjectionAndConsumption
            ? 'solarPanels'
            : isCombinedOrCustomerSwitch
              ? !lastSettlementDateYearAfterContractStart
                ? 'move'
                : undefined
              : undefined
      },
      consumption: {
        showContent: (effectiveContract && atLeastAMonthOldEL) || electricity.detail.status === CONTRACT_STATUSES.terminated
      },
      instalment: {
        canAccess: !isProducer && !isBoltGo && !electricity.detail.monthlyBilling,
        showContent: activeOrEffectiveContractEL
      },
      meterReadings: {
        canAccess: {
          view: {
            [ENERGY_TYPES.electricity]: canViewMeterReadingsEL,
            [ENERGY_TYPES.gas]: canViewMeterReadingsNG
          },
          edit: {
            [ENERGY_TYPES.electricity]: canViewMeterReadingsEL && !electricity.deliveryPoint.meteringRegime,
            [ENERGY_TYPES.gas]: canViewMeterReadingsNG && !gas?.deliveryPoint?.meteringRegime
          }
        },
        showContent: (activeOrEffectiveContractEL && atLeastAMonthOldEL) || (activeOrEffectiveContractNG && atLeastAMonthOldNG)
      },
      marketPrices: {
        todayTomorrow: {
          canAccess: !!electricity.detail.dynamicTariff
        },
        historic: {
          canAccess: !checkHasFixedProduct(electricity)
        }
      }
    }
  } catch (error) {
    log({
      error: error instanceof Error ? error : 'unknown error determining access rights',
      identifier: `[${Flow.ACCESS_RIGHTS}]`,
      criticality: Criticality.HIGH,
      metaData: {
        access_rights_data: {
          electricity,
          gas,
          isProducer,
          lastSettlementDate,
          currentOpportunity
        }
      }
    })

    return INITIAL_ACCESS_RIGHTS_STATE
  }
}
